import {
  angleModeFor, visibleTargeted, rolesForMode, isReachable, displayEvaluation,
  buildScaleValueTrigger, parseScaleValueTrigger, scaleValueTriggerMatches,
  evaluateScale, evaluateEquation, resolveMatchedBranch, resolveEffectiveValues, scaleBranchMissingCorrect,
  SCALE_MIN_VALUES, SCALE_MAX_VALUES,
  addScaleVersion, applyScaleVersion, branchSetsToCheck, removeScaleVersion, versionRollKey, writeScaleVersion,
  scaleReachability, branchesUnreachableInRange, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount, activeFeedbackForBranch, pickScenarioBranch, resolveScenarioValues, unreviewedCount, drawnScenarioId } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Minus, Scale, Trash2, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useArmedDelete } from './block-actions';
import { type BlockIssue, type IssueCache, type IssueContext, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, CollapsedBlockBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass, UnreachableChip } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, ChipDivider, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { stripHtmlForLabel } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { OptionsSection } from './options-section';
import { RichTextEditor } from './rich-text-editor';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { StrandWarning, useArmSettle } from './strand-warning';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenRowDeleteWarning } from './use-driven-row-dependents';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { makeRowBranch, nestedBranching, type SeedMode } from './use-nested-branching';
import { useRestoredData } from './use-restored-data';
import { useScenarioFollower } from './use-scenario-follower';
import { useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockQuestion, AnswerDisplayFrame, hasRollableVariables, htmlHasVariableChips, PreviewHtml, rollVariableValues, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { AngleMode, ConditionalRoleBranch, ScenarioRoleBranch, ScaleBlockData, ScaleValue, FeedbackState } from '@agavi/blocks';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskPreviewRoll, useTaskVariables } from '@/contexts/task-variables-context';
import { catchAllTitle, catchAllChips, scaleChips } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { scaleCorrectChipLabel, scaleTriggerLabel, scaleValueLabel } from '@/lib/block-state-labels';

/** Bridges "jump to this branch" from the live-test widget to the editor — two separately mounted
 * instances of this component, with no other channel to hand a value across. Identical to the
 * mechanism Dropdown/Keyword/FITB carry. Deliberately NOT automatic on a reroll: it fires only when
 * the teacher clicks the jump action. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface ScaleBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by Preview's single page-level Submit — see the effect that consumes it. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

interface TriggerOption {
  value: string;
  label: string;
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}

type GradingMode = ScaleBlockData['gradingMode'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** A fresh axis. Points start `incorrect` in graded mode (not one pre-seeded correct) so the
 * "No Correct Answers" error shows until the teacher marks one — deliberate, so a block cannot look
 * configured when it isn't. */
function makeValues(count: number, gradingMode: GradingMode): ScaleValue[] {
  const role: ScaleValue['role'] = gradingMode === 'graded' ? 'incorrect' : 'neutral';
  return Array.from({ length: count }, (_, i) => ({ value: i + 1, label: '', role }));
}

/**
 * Reads both the current single-axis shape and the multi-row shape that preceded it.
 *
 * Rows were removed 2026-08-12 (see the note on `ScaleBlockData`): a row's label lived outside the
 * question, so an attempt could not say which row it answered. Every Scale block in the wild was
 * already single-row, so the migration is to lift `rows[0]`'s values and fold the matching
 * `columnHeaders[i]` into each point's own `label` — after which header and point are one thing.
 * A multi-row block would lose rows 2+; there are none, and inventing a split here would be
 * guesswork about content.
 */
/**
 * Drops targeted states left over from the "All Incorrect" / "All Neutral" catch-alls.
 *
 * Their trigger names a role, and nothing answers to that any more, so the state can never fire.
 * Leaving it in place is the worse option: the trigger picker only lists points, so `find(...) ??
 * options[0]` would show the state pointing at some arbitrary point it was never authored for,
 * which reads as authored intent rather than as debris. Dropping is safe because the catch-alls
 * only ever existed in this branch's own test data (see feedback_legacy_trigger_names_disposable).
 */
function dropRoleCatchAlls(feedback: ScaleBlockData['feedback']): ScaleBlockData['feedback'] {
  const states = feedback.states.filter(s => !(s.type === 'targeted' && s.trigger.startsWith('role_')));
  return states.length === feedback.states.length ? feedback : { ...feedback, states };
}

function initData(raw: Record<string, unknown>): ScaleBlockData {
  const gradingMode: GradingMode = ['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
    ? raw.gradingMode as GradingMode
    : 'graded';
  const feedback = dropRoleCatchAlls((raw.feedback ?? { states: [] }) as ScaleBlockData['feedback']);
  // A branch persisted before `feedback` was part of ConditionalRoleBranch has no such field, and
  // every read of `branch.feedback.states` would throw on it. Backfilling from the block's own
  // top-level feedback is what Dropdown/Keyword/FITB all do here, for the same reason.
  type RawBranch = Omit<ConditionalRoleBranch, 'feedback'> & { feedback?: ScaleBlockData['feedback'] };
  const rawBranches = raw.conditionalBranches as RawBranch[] | undefined;

  let values = raw.values as ScaleValue[] | undefined;
  if (!values?.length) {
    const legacyRows = raw.rows as { values?: { value: number; role: ScaleValue['role'] }[] }[] | undefined;
    const legacyHeaders = (raw.columnHeaders as string[] | undefined) ?? [];
    const count = Math.max(SCALE_MIN_VALUES, Math.min(SCALE_MAX_VALUES, (raw.choiceCount as number) || 5));
    const legacy = legacyRows?.[0]?.values?.filter(v => v.value <= count);
    values = legacy?.length
      ? legacy.map(v => ({ value: v.value, role: v.role, label: (legacyHeaders[v.value - 1] ?? '').trim() }))
      : makeValues(count, gradingMode);
  }

  return {
    question: (raw.question as string) ?? '',
    values,
    reverseOrder: (raw.reverseOrder as boolean) ?? false,
    gradingMode,
    feedback,
    conditionalEquation: raw.conditionalEquation as string | undefined,
    conditionalBranches: rawBranches?.map(b => ({ ...b, feedback: b.feedback ? dropRoleCatchAlls(b.feedback) : feedback })),
    conditionalBranchesDraft: raw.conditionalBranchesDraft as ScaleBlockData['conditionalBranchesDraft'],
    angleMode: (raw.angleMode as AngleMode | undefined) ?? 'degrees',
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    /* Same hazard: a scenario link read back as absent is a block that silently stops
       following, with its per-scenario answers orphaned behind it. */
    followsBlockId: raw.followsBlockId as ScaleBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as ScaleBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as ScaleBlockData['scenarioBranches'],
    /* Builds a NEW object field by field, so anything not named is dropped — which is how a bank
       comes to be created, written to Mongo and then silently gone. */
    versions: raw.versions as ScaleBlockData['versions'],
  };
}

/** Nothing is markable-correct until the teacher marks it, so this is the whole answer-setup check —
 * no 'partial' state now that there is one axis rather than N rows. */
function hasCorrectValue(values: ScaleValue[]): boolean {
  return values.some(v => v.role === 'correct');
}

// ─── Standardized issue reporting ──────────────────────────────────────────────
// Pass A adopted the shared issue-bar FRAME and nothing else, deferring real checks to this block's
// deep audit. This is that audit. The one list read by the collapsed card's bar, the editor's
// warning bar, and the test panel's diagnostics, so those three can never word the same problem
// differently or disagree about whether it exists.
//
// TWO STANDARD CHECKS ARE DELIBERATELY ABSENT, because Scale's trigger model makes them vacuous
// rather than because they were skipped:
//
// - `feedback-overlaps-correct` (the partial-overlap warning Angle/Compass/GPS carry) has no
//   Scale-shaped case. Those blocks target continuous ranges, which can clip the accepted band at
//   one end. A Scale value trigger names exactly one value: it is either correct (fully subsumed,
//   reported below) or it is not (no overlap at all). There is no in-between to warn about.
// - A value trigger and its row's role catch-all covering the same value is NOT an overlap. It is
//   the carve-out the block is built around, and `evaluateScale` fires both on purpose (see
//   `valueCoveredByTrigger`). Reporting it would be telling a teacher that the intended feature is
//   a mistake, so `feedback-overlap` here means two states with the IDENTICAL trigger.
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedScaleData(
  rawD: ScaleBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): ScaleBlockData {
  const rawVersioned = applyScaleVersion(rawD, versionId);
  return { ...rawVersioned, feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode) };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `scaleIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function scaleBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return scaleIssues(buildNormalizedScaleData(initData(block.data), ctx.t), ctx);
}

export function scaleIssues(d: ScaleBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryFeedback = t('blocks.areas.feedback');
  const categoryBranches = t('blocks.areas.alternativeAnswerSetup');
  const targeted = d.feedback.states.filter(s => s.type === 'targeted');
  // Every role question below asks about the mode the block is actually in. Outside graded nothing
  // is correct, so "this feedback targets a value now marked correct" cannot arise — it used to,
  // and it named a correctness the mode does not have.
  const values = rolesForMode(d.gradingMode, d.values);

  // A rich-text field's empty state serializes to `<p></p>`, so the tags come off before the
  // trim — same guard every other block's question check uses.
  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }
  /* UNREVIEWED ROWS, counted — the strip inside the answer-setup box only speaks for the row
     you are on, so a teacher who never pages to scenario 5 never learns it was never decided. */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: t('blocks.areas.answerSetup'),
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  // One axis, so this is a plain yes/no — the old 'partial' state (some rows configured, some
  // not) has nothing left to describe.
  if (d.gradingMode === 'graded' && !hasCorrectValue(d.values)) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion',
      category: t('blocks.areas.answerSetup'),
      detail: t('blocks.answers.noCorrectAnswers') });
  }

  // Two states on the identical trigger. The trigger picker already filters a taken option out of
  // the list, so this cannot be authored today — it is a data-integrity check for a block that
  // arrived by some other route, and it is cheap.
  const triggerCounts = new Map<string, number>();
  for (const s of targeted) triggerCounts.set(s.trigger, (triggerCounts.get(s.trigger) ?? 0) + 1);
  if ([...triggerCounts.values()].some(n => n > 1)) {
    issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.scale.feedbackOverlapWarning') });
  }

  // A value trigger whose value has since been marked correct can never fire, and today it does not
  // merely fail quietly: the block HIDES it from the feedback panel entirely (see the
  // useInputBlockFeedback call), so a teacher's authored feedback disappears with no explanation
  // while still being stored. Saying so is the whole point of this check.
  if (targeted.some(s => {
    const value = parseScaleValueTrigger(s.trigger);
    return value !== null && values.find(v => v.value === value)?.role === 'correct';
  })) {
    issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.scale.feedbackSubsumedWarning') });
  }

  // Branch diagnostics. No grace period and no gate on the static setup being complete: branching
  // is an explicit opt-in, so anything wrong with it was authored deliberately and is worth saying
  // immediately. Same shape and severities as Dropdown/Keyword/FITB carry.
  /* PER VERSION: these checks read version-resolved data, so a banked block was only ever
     validated for the version being displayed. See `branchSetsToCheck`. */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalRoleBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryBranches, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryBranches, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gaps = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlaps = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gaps.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryBranches, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gaps, t, 'gap') })) });
        }
        if (overlaps.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryBranches, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlaps, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryBranches,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryBranches,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryBranches,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        // Correctness is AND-across-rows, so a branch's completeness is a per-row question — name the
        // rows still missing an answer rather than reporting one all-or-nothing boolean.
        if (scaleBranchMissingCorrect(b, d.values)) {
          issues.push({ key: vkey(`branch-${b.id}-none`), severity: 'error', kind: 'incompletion', category: categoryBranches, detail: vlabel(t('blocks.scale.diagnosticBranchNoCorrect', { number: i + 1 })) });
        }
        if (b.operator === 'between') {
          const lo = parseFloat(b.threshold);
          const hi = parseFloat(b.thresholdHigh ?? '');
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryBranches, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Every branch's own feedback set counts here too — a branch owns its messages, so a blank one is
  // just as missing as a blank top-level one and would otherwise go unreported.
  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  if (hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) || d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states))) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion',
      category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }
  return issues;
}

/** Re-exported under this block's own name — the derivation lives in `lib/block-state-labels.ts`
 * so Flow and the test legend resolve a value to the same string this editor does. */
const valueDisplayLabel = scaleValueLabel;

/**
 * `scaleValueLabel` as a NODE, for the chip legend. The string version flattens a rich label with
 * stripHtmlForLabel, which turns a variable into literal `{name}` — correct for a stored feedback
 * title and not acceptable on screen.
 *
 * Translated FIRST and the label substituted after, via a sentinel: assembling the sentence from
 * fragments would hard-code English word order, and `valueWithLabel` puts the value and the label
 * in different places in different locales. Same reasoning as chipifyFormulaValues.
 */
/** Both moved to `lib/block-condition-chips` — see that file's note. */

/*
 * Value runs used to collapse here: consecutive same-role values became a single "4-9" range chip,
 * on the theory that a long flat list hid where the correct/incorrect boundary fell.
 *
 * Removed, because a range describes something a student cannot do. A Scale row accepts exactly one
 * pick, so every value is an independently selectable answer and every chip should name one of them
 * — the same rule MCQ and Dropdown already follow for their options. "Row 1: Value 4-5" reads as a
 * span you could land anywhere inside; "Row 1: Value 4" and "Row 1: Value 5" are the two answers
 * that actually exist. The `valueRangeLabel` string it needed is gone with it.
 */

// ─── Trigger helpers ────────────────────────────────────────────────────────────
// Two independent kinds of targeted trigger, freely combinable rather than mutually exclusive: a
// specific point, or an "every point currently marked this role" catch-all (dynamic — re-resolved
// against each point's *current* role at evaluation time, never a frozen snapshot). A teacher can
// carve out one specific wrong answer's own feedback while still catching everything else via the
// role trigger; both are allowed to match the same submission at once (see evaluateScale).

/** True once the trigger can no longer possibly fire: its point has since been marked 'correct', or
 * removed from the axis entirely. */
function isTriggerFullyCorrect(trigger: string, values: ScaleValue[]): boolean {
  const value = parseScaleValueTrigger(trigger);
  if (value === null) return false;
  const point = values.find(v => v.value === value);
  return !point || point.role === 'correct';
}

/** True if some *other* targeted state already claims this exact point, so the picker never offers
 * the same point twice. */
function valueCoveredByTrigger(states: FeedbackState[], value: number, excludeStateId?: string): boolean {
  return states.some(s => s.type === 'targeted' && s.id !== excludeStateId && scaleValueTriggerMatches(s.trigger, value));
}

/** The option list for one feedback state's trigger dropdown: every non-correct point not already
 * claimed by another state, and nothing else. One trigger per point, the same shape MCQ and Dropdown
 * use for their options — "a message for all the wrong answers at once" is what Default Incorrect
 * is, not a trigger. */
function buildScaleTriggerOptions(values: ScaleValue[], states: FeedbackState[], excludeStateId: string | undefined, t: (key: string, opts?: Record<string, unknown>) => string): TriggerOption[] {
  return values.flatMap(v => {
    if (v.role === 'correct' || valueCoveredByTrigger(states, v.value, excludeStateId)) return [];
    return [{
      value: buildScaleValueTrigger(v.value),
      label: valueDisplayLabel(v.value, values, t),
      evaluation: (v.role === 'incorrect' ? 'incorrect' : 'neutral') as TriggerOption['evaluation'],
    }];
  });
}

const triggerSummary = scaleTriggerLabel;

/** Re-derives every targeted feedback title against the current points — needed
 * whenever a point is removed or renumbered, or a label changes (trigger labels resolve through it).
 * The title IS `triggerSummary`'s own label — the same one shown in the trigger-picker dropdown — so
 * it always reflects exactly which point a feedback targets, never independently editable text that
 * could drift. */
function resyncScaleTargetedTitles(values: ScaleValue[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted') return s;
    const summary = triggerSummary(s.trigger, values, t);
    return summary ? { ...s, title: summary.label } : s;
  });
}

/** Targeted feedback's evaluation always mirrors what its trigger actually targets, derived from
 * that point's current role so the two can never silently drift apart — same fix as Dropdown's. */
function resyncScaleTargetedEvaluations(values: ScaleValue[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted') return s;
    const value = parseScaleValueTrigger(s.trigger);
    if (value === null) return s;
    const point = values.find(v => v.value === value);
    if (!point || point.role === 'correct') return s;
    return { ...s, evaluation: point.role };
  });
}

// ─── Live test data ───────────────────────────────────────────────────────────
// One shared derivation, called from 3 places (desktop collapsed card, phone Test tab, Preview) —
// the same "canvas card becomes a live test surface" pattern as FITB/Dropdown/MCQ/Keyword.
//
// `evaluateScale` has existed, fully written, since this block was built and has never been called
// from the UI at all. Nothing here re-implements it: the chips describe what it *would* do, and the
// `active` flags are read off its actual return value, so the legend can't drift from the grading.

interface ScaleTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  matchedBranch: ConditionalRoleBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
  hasChips: boolean;
  hasRollable: boolean;
}

/**
 * Whether anything the student sees carries a variable chip — the reroll question, "is there
 * anything worth rolling". Labels count: they render through `PreviewHtml`, which resolves chips
 * against the ambient roll, so a scale whose only chip sits in a label really does have something to
 * reroll and would otherwise show rolled values with no way to roll them again.
 *
 * NOT the branching question. Whether "Correctness depends on variables" is offered stays gated on
 * the question ALONE — see `questionHasChips` — because a label is decoration on a point, not a
 * thing correctness can turn on. The two look similar and must not be merged.
 */
function scaleHasChips(d: ScaleBlockData): boolean {
  return htmlHasVariableChips(d.question) || d.values.some(v => htmlHasVariableChips(v.label));
}

function computeScaleTestData(
  d: ScaleBlockData,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submitted: number | null,
  hasSubmitted: boolean,
  /** The leader's variant in force — see `evaluateDropdown`'s own note. */
  scenarioId?: string,
): ScaleTestData {
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  // Derived from `d`, NOT from editor state: the unselected canvas card is a SEPARATE mounted
  // instance whose own state was captured at mount and never updates when block.data changes, so an
  // editor-state read is stale exactly there. Found live — the reroll button never appeared on the
  // card.
  const hasChips = scaleHasChips(d);
  const hasRollable = hasRollableVariables(variables);
  const testResult = hasSubmitted ? evaluateScale({ value: submitted }, d, resolvedVariables, scenarioId) : null;
  // A FOLLOWER resolves against the scenario in force instead of an equation — the same split
  // `evaluateScale` makes internally, mirrored because this builds what the teacher SEES.
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch(d.scenarioBranches!, scenarioId) : null;
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  // Branches are graded-only, matching evaluateScale's own gate.
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  // Every chip describes the answer key actually in force, so the legend is built from the RESOLVED
  // points and the branch's own feedback set, not the block's static ones.
  const values = d.gradingMode === 'graded'
    ? (followed
      ? resolveScenarioValues(d.values, d.scenarioBranches, scenarioId)
      : resolveEffectiveValues(d.values, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation')))
    : d.values;
  const states = (scenarioRow?.feedback ?? matchedBranch?.feedback ?? d.feedback).states;
  const testChips: TestBarChip[] = [];
  const push = (c: TestBarChip) => testChips.push(c);

  /**
   * Participation: the targeted states FIRST, then the catch-all naming what is left.
   *
   * Both arms used to list the catch-all alone and return, so a block with a rating targeted for
   * its own feedback showed one chip reading "Any Response" — the panel and the bar describing
   * the same block differently, which is the one thing the shared chip derivations exist to stop.
   */
  if (d.gradingMode !== 'graded') {
    if (d.gradingMode === 'survey') {
      const unanswered = states.find(x => x.trigger === 'unanswered');
      if (unanswered) push({ id: unanswered.id, condition: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation: unanswered.evaluation, active: firedIds.has(unanswered.id), category: 'unanswered' });
    }
    const participationReach = scaleReachability(d, values);
    let anyTargeted = false;
    states.forEach(st => {
      if (st.type !== 'targeted') return;
      const summary = triggerSummary(st.trigger, values, t);
      if (!summary) return;
      anyTargeted = true;
      push({ id: st.id, condition: summary.label, evaluation: 'neutral', active: firedIds.has(st.id), category: 'targeted' });
    });
    /*
     * OPTIONAL is ONE ROW. Everything accumulated above belongs to a mode that can fire it, and
     * optional fires none of it: the mode is inert, so Any Response is the only outcome it has.
     * Cleared rather than never-pushed, because the rungs above are shared with survey, which
     * does fire them. `legendStatesFor` makes the same call for the blocks that use it.
     */
    if (d.gradingMode === 'optional') testChips.length = 0;
    const catchAll = states.find(x => x.trigger === (d.gradingMode === 'optional' ? 'any_response' : 'any_answer'));
    if (catchAll) {
      testChips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, anyTargeted, t,
          () => scaleChips('incorrect', participationReach, values, t, d.gradingMode)), {
          id: catchAll.id,
          category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    return { testResult, testChips, matchedBranch, matchedBranchIndex, matchedEquationValue, hasChips, hasRollable };
  }

  const unansweredState = states.find(x => x.trigger === 'unanswered');
  if (unansweredState) push({ id: unansweredState.id, condition: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation: unansweredState.evaluation, active: firedIds.has(unansweredState.id), category: 'unanswered' });

  // What was actually picked, resolved the way evaluateScale resolves it.
  const picked = values.find(v => v.value === submitted);
  const correctState = states.find(x => x.trigger === 'correct');
  const incorrectState = states.find(x => x.trigger === 'incorrect');
  const correctFired = correctState ? firedIds.has(correctState.id) : false;
  const incorrectFired = incorrectState ? firedIds.has(incorrectState.id) : false;

  // Correct — one chip per accepted point, never a collapsed range: a student picks exactly one
  // point, so every value is its own selectable answer and a "4-5" chip would describe a span
  // nobody can choose.
  const barReach = scaleReachability(d, values);
  const chipEnvelope = (id: string, category: 'correct' | 'incorrect', active: boolean) => ({
    id, category, active,
    // Only the position the student actually picked lights up, not the whole row.
    activeSourceIds: new Set(picked ? [String(picked.value)] : []),
    unreachableLabel: t('blocks.feedbackPanel.unreachable'),
    connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
  });
  if (correctState) {
    // Any one of the accepted positions satisfies it — a band of correct positions is the ordinary
    // way to author this block, and both surfaces print "or" between them.
    testChips.push(...toTestBarChips(
      scaleChips('correct', barReach, values, t), chipEnvelope(correctState.id, 'correct', correctFired)));
  }

  // Targeted — one chip per targeted state, of either kind. A value trigger and the role catch-all
  // are deliberately allowed to fire together (see evaluateScale).
  states.forEach(s => {
    if (s.type !== 'targeted') return;
    const summary = triggerSummary(s.trigger, values, t);
    if (!summary) return;
    push({ id: s.id, condition: summary.label, evaluation: s.evaluation, active: firedIds.has(s.id), category: 'targeted' });
  });

  // Default Incorrect's remaining catchment: every non-correct point with no targeted trigger over
  // it. Unlike FITB there is no "any other answer" chip — the answers are a finite enumerated set,
  // so these are exhaustive and a catch-all would describe a case that cannot occur.
  if (incorrectState) {
    testChips.push(...toTestBarChips(
      scaleChips('incorrect', barReach, values, t), chipEnvelope(incorrectState.id, 'incorrect', incorrectFired)));
  }

  return { testResult, testChips, matchedBranch, matchedBranchIndex, matchedEquationValue, hasChips, hasRollable };
}

/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderScaleFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} />;
}

/**
 * The one canonical "what a student sees" layout — question, then the answerable grid, then fired
 * feedback. Shared by all three live mounts (Preview, the selected canvas card, the phone Test tab)
 * so they cannot drift; same role as `FitbAnswerDisplay` and every other block's `AnswerDisplay`.
 */
function ScaleAnswerDisplay({ d, selected, onSelect, rolledValues, children, scenario }: {
  d: ScaleBlockData;
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`. */
  scenario?: React.ReactNode;
  selected: number | null;
  onSelect: (value: number) => void;
  /** One roll, provided to the whole subtree. The question, every row label, and the fired-feedback
   * bodies below all render through `PreviewHtml`, so they resolve against the same values without
   * any of them being handed the roll individually. */
  rolledValues: Record<string, string>;
  children?: React.ReactNode;
}) {
  return (
    <AnswerDisplayFrame question={d.question} rolledValues={rolledValues} scenario={scenario}>
      <ScaleWidgetPreview
        values={rolesForMode(d.gradingMode, d.values)}
        reverseOrder={d.reverseOrder}
        // Never the answer key on a surface someone is answering — same reasoning MCQ records for
        // deliberately not coloring an option by its role.
        revealAnswers={false}
        compact={false}
        selected={selected}
        onSelect={onSelect}
      />
      {children}
    </AnswerDisplayFrame>
  );
}

/**
 * True when a label holds nothing a student would see.
 *
 * The trap that makes this a function rather than `.trim()`: an emptied RichTextEditor serialises to
 * `<p></p>`, never `''`. Compared naively that is a non-empty string, so every point would claim a
 * label, `anyLabel` would always be true, and the axis would grow a blank strip above it forever.
 * Marks alone don't count either — `<p><strong></strong></p>` is still empty. A formula or a
 * variable chip DOES count even with no text, since both render something.
 */
function labelIsEmpty(label: string): boolean {
  if (/<(img|span[^>]+data-(math-latex|reaction-formula|variable))/i.test(label)) return false;
  return stripHtmlForLabel(label).trim() === '';
}

// ─── Widget (shared by isPreview, the collapsed card and the phone Test tab) ───

/**
 * The gap between points, and how far it may close when the axis is squeezing onto one line.
 *
 * Spacing is decoration; the size of the thing you tap is function. So when the two compete the gap
 * gives way first — 2px of air is worth much less than the 2px of button it buys.
 */
const AXIS_GAP_PX = 6;
const AXIS_TIGHT_GAP_PX = 4;

/**
 * How narrow a point is allowed to get when the axis squeezes to fit one line.
 *
 * 44px is Apple's HIG minimum and WCAG 2.5.5 (AAA); WCAG 2.2's AA floor is only 24px, but these are
 * the answer controls on a phone in a field, often tapped one-handed by someone wearing gloves or
 * standing in the sun, so the stricter number is the right one. Below this the axis wraps instead —
 * a second line is a much smaller cost than a point that is hard to hit.
 */
const MIN_TOUCH_PX = 44;

/**
 * Bounds on where the axis may start. Wide enough for anything real (no ordinal scale in school
 * science starts below zero, let alone at -99) and narrow enough that a typo cannot push the point
 * numbers somewhere they stop being readable in a 44px button.
 */
const SCALE_FIRST_MIN = -99;
const SCALE_FIRST_MAX = 99;

/**
 * How many columns the axis should use, so that wrapping splits the points EVENLY.
 *
 * Plain `flex-wrap` fills each line to capacity before starting the next, so 15 points in a row that
 * fits 7 come out 7 / 7 / 1 — a lone orphan under a full row, which reads as a mistake rather than a
 * scale. Balancing is the same idea as `text-wrap: balance`, which has no flexbox equivalent: keep
 * the line count wrapping would have produced, then divide the points evenly across that many lines.
 *
 *   15 points, 7 fit  ->  3 lines  ->  ceil(15/3) = 5 columns  ->  5 / 5 / 5
 *   15 points, 8 fit  ->  2 lines  ->  ceil(15/2) = 8 columns  ->  8 / 7
 *
 * Never fewer lines than would fit, so this only ever evens a wrap out — it cannot force one.
 * Returns null until the width is known; the caller falls back to ordinary wrapping for that frame
 * rather than flashing a one-column axis.
 */
function useAxisLayout(
  ref: React.RefObject<HTMLElement | null>,
  { count, cellPx, gapPx, canShrink }: { count: number; cellPx: number; gapPx: number; canShrink: boolean },
): { columns: number; cellPx: number; gapPx: number } | null {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // ResizeObserver, not a window listener: the axis also changes width when the options drawer
    // opens or the card moves between the canvas and the phone Test tab, and neither resizes the
    // window.
    const ro = new ResizeObserver(([entry]) => { setWidth(entry!.contentRect.width); });
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, [ref]);

  if (width === null || count === 0) return null;

  // With no labels there is nothing above a point that needs the width, so the axis would rather be
  // one unbroken line of smaller buttons than two lines of larger ones — a scale reads as a scale
  // when you can see the whole run of it at once. Only down to the touch floor, and never wider than
  // the default, which is already comfortable.
  if (canShrink) {
    // Try the normal gap first and only close it if that is what buys the single line, so a wide
    // axis keeps its air and a tight one spends it where it counts.
    for (const gap of [gapPx, AXIS_TIGHT_GAP_PX]) {
      const even = Math.floor((width - gap * (count - 1)) / count);
      if (even >= MIN_TOUCH_PX) return { columns: count, cellPx: Math.min(cellPx, even), gapPx: gap };
    }
  }

  // n cells span n*cell + (n-1)*gap, so solve for n. Floor, and never below 1.
  const fit = Math.max(1, Math.floor((width + gapPx) / (cellPx + gapPx)));
  const lines = Math.ceil(count / fit);
  const columns = Math.ceil(count / lines);

  // Then spend whatever is left over on the cells themselves. The default width is a floor, not a
  // target: pinning cells to it left a 481px axis rendering 56px columns, so a label with any real
  // content wrapped far harder than it needed to. Uniform, so the axis stays aligned — every column
  // grows by the same amount — and capped, because past a point a wider column stops helping a short
  // label and just spreads the axis apart.
  const spare = Math.floor((width - gapPx * (columns - 1)) / columns);
  return { columns, cellPx: Math.max(cellPx, spare), gapPx };
}

/**
 * The axis a student answers: a labelled point row above, a button per point below.
 *
 * The fixed `w-28` label gutters that used to flank this are gone with the rows. They were the
 * layout catastrophe: two of them, reserved unconditionally whether or not a row had labels, taking
 * 224px of a 291px card on phone portrait and leaving the axis — the only part that captures an
 * answer — **9 pixels**. With one axis and labels sitting above their own point, the buttons get the
 * full width and simply wrap when there are more points than fit.
 */
function ScaleWidgetPreview({ values, reverseOrder, revealAnswers, compact, selected, onSelect }: {
  values: ScaleValue[];
  reverseOrder: boolean;
  revealAnswers: boolean;
  /**
   * The student's pick. Orthogonal to `revealAnswers`: that one is a TEACHER affordance showing the
   * authored roles, this is what someone answering has chosen. Both can be on at once — a teacher
   * testing wants to see the key and their own pick — so nothing here may fold the two together.
   */
  selected?: number | null;
  /**
   * Supplying this is what makes the widget answerable. Absent, every cell stays an inert `<span>`
   * and the tree keeps `aria-hidden` — which is what the unselected canvas card still wants.
   */
  onSelect?: (value: number) => void;
  /** The collapsed canvas card: tighter, so a block stays easy to grab and reorder. */
  compact: boolean;
}) {
  const { t } = useTranslation();
  /**
   * Answerable exactly when someone is listening. Deriving it from `onSelect` rather than a separate
   * flag makes the two impossible to disagree — there is no way to render controls that report
   * nowhere, or to swallow a pick that had a handler waiting.
   */
  const interactive = !!onSelect;
  const cellRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const axisRef = useRef<HTMLDivElement | null>(null);

  // reverseOrder is display-only: value 1 is still value 1 in the data, it just renders at the other
  // end, so labels travel with their own point rather than being re-indexed.
  const shown = reverseOrder ? [...values].reverse() : values;
  const anyLabel = values.some(v => !labelIsEmpty(v.label));

  // Roving tabindex: the selected point is the group's single tab stop, or the first when nothing is
  // picked yet. Copied from MCQAnswerDisplay rather than reinvented.
  const selectedIdx = shown.findIndex(v => v.value === selected);
  const focusIdx = selectedIdx >= 0 ? selectedIdx : 0;

  function handleKeyDown(e: React.KeyboardEvent, idx: number) {
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % shown.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (idx - 1 + shown.length) % shown.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = shown.length - 1;
    if (next === null) return;
    e.preventDefault();
    const target = shown[next]!;
    onSelect?.(target.value);
    // Focus has to move WITH the selection or it strands on a cell that just became tabIndex={-1},
    // and the parent owns `selected`, so the new node is not guaranteed to exist synchronously.
    requestAnimationFrame(() => { cellRefs.current[target.value]?.focus(); });
  }

  const cellSize = compact ? 'h-8' : 'h-10';
  const layout = useAxisLayout(axisRef, {
    count: shown.length,
    // A fixed width, deliberately: a label wraps inside its column, it does not widen it. Sizing
    // columns to the longest label instead cost a whole extra ROW the moment a 7th point was added,
    // and a point pushed onto another line is a worse trade than a label on two. Formulas used to be
    // the exception that forced it — they could not wrap at all — which is what the reaction
    // splitting in chem-formula-extension fixed.
    cellPx: compact ? 56 : 64,
    gapPx: AXIS_GAP_PX,
    // Squeezing to the touch floor is for a BARE axis. With labels it is actively worse: at 44px the
    // labels collide with each other and a formula, which cannot wrap below its own widest unit,
    // spills across its neighbours. Labelled axes take the balanced-wrap path instead, which then
    // spends the leftover width on the columns it did keep.
    canShrink: !anyLabel,
  });

  return (
    // aria-hidden and pointer-events-none only while inert. An answerable widget must be reachable
    // by both a pointer and a screen reader.
    <div aria-hidden={interactive ? undefined : 'true'} className={`flex flex-col gap-1 ${interactive ? '' : 'pointer-events-none'}`}>
      <div
        {...(interactive ? { role: 'radiogroup' as const, 'aria-label': t('blocks.scale.axisLabel') } : {})}
        ref={axisRef}
        // A grid on a measured column count, so a wrap splits the points evenly (see
        // useBalancedColumns) instead of orphaning the last one under a full row. Falls back to
        // flex-wrap for the first frame, before the width is known.
        //
        // Centred, and the grid is what makes that safe: justify-content moves the whole track group
        // at once, so the columns stay locked to each other and a short last row still sits under
        // the columns above it. Centering the old flex-wrap was a stagger, because it re-centred
        // each line independently — 1-9 flush left, 10-15 floating in the middle under them. The
        // pre-measurement frame stays justify-start for exactly that reason.
        //
        // items-end is what keeps the buttons on one line. Each cell is [label, button] stacked, so
        // a label that wraps to four lines makes its own cell taller — with the default `stretch`
        // that pushed its button down out of line with its neighbours. Bottom-aligning the cells
        // pins every button to the same baseline and lets the labels grow upward instead.
        className={`items-end ${layout === null ? 'flex flex-wrap justify-start gap-1.5' : 'grid justify-center'}`}
        style={layout === null ? undefined : { gap: layout.gapPx, gridTemplateColumns: `repeat(${layout.columns}, max-content)` }}
      >
        {shown.map((v, i) => {
          const isPicked = selected === v.value;
          // The accessible name has to be plain text — a screen reader gets the label flattened the
          // same way a chip does, rather than markup or nothing.
          const plainLabel = stripHtmlForLabel(v.label).trim();
          // EXCEPT when the label is a formula. stripHtmlForLabel substitutes a formula's raw
          // source, which is right for a chip a teacher reads but is gibberish spoken aloud —
          // "backslash sum underscore zero caret open brace backslash infinity". KaTeX already
          // emits proper MathML for this, and marks its visual output aria-hidden so the two are
          // never both read; an aria-label here overrides that and throws the MathML away. So for
          // a formula label the name is left to the button's own content, which is the number plus
          // that MathML.
          const labelIsFormula = /data-(math-latex|reaction-formula)/.test(v.label);
          const Icon = v.role === 'correct' ? Check : v.role === 'incorrect' ? X : Minus;
          // An empty label renders NOTHING above its point — the number is already on the button, so
          // repeating it overhead says nothing. That is also what makes two-word anchoring work:
          // fill only the first and last labels and the middle stays clean.
          // Every cell is the SAME width whatever its label, so the axis stays evenly spaced instead
          // of staggering around whichever points happen to be labelled. The label wraps inside that
          // width rather than widening its own cell.
          const cell = (
            <span className='flex flex-col items-center gap-0.5' style={{ width: layout?.cellPx ?? (compact ? 56 : 64) }}>
              {/* text, not muted: the label is content a student reads, not chrome.
                  overflow-wrap:anywhere rather than break-words — a long unbroken string (a chemical
                  name, a URL) has no space to break at, and break-words would let it overflow the
                  cell and stagger the axis it is meant to sit inside. */}
              {anyLabel && (
                <span className='flex min-h-8 w-full items-end justify-center px-0.5'>
                  <PreviewHtml
                    as='span'
                    html={labelIsEmpty(v.label) ? '' : v.label}
                    // [&_p]:inline so the paragraph TipTap always wraps content in cannot force a
                    // line break inside a 64px cell. The rest is MCQ's own inline-mark list.
                    className='scale-point-label text-center text-xs leading-tight text-ws-text [hyphens:auto] [overflow-wrap:anywhere] [&_p]:inline [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_u]:underline [&_s]:line-through [&_sub]:align-sub [&_sub]:text-[0.75em] [&_sup]:align-super [&_sup]:text-[0.75em]'
                  />
                </span>
              )}
              <span className={`flex ${cellSize} w-full shrink-0 items-center justify-center gap-0.5 rounded-md border text-xs font-semibold ${
                // A picked option fills in with a check, exactly like the revealed answer key does —
                // the same affordance MCQ and Dropdown use for a chosen option. A ring around it read
                // as a different language from every other block.
                (revealAnswers ? v.role === 'correct' : isPicked)
                  ? 'border-ws-primary bg-ws-primary text-ws-btn-on-primary'
                  : revealAnswers && v.role === 'incorrect' ? 'border-ws-border bg-ws-muted/20 text-ws-muted'
                  : 'border-ws-border bg-ws-btn text-ws-muted'
              }`}>
                {revealAnswers
                  ? <Icon aria-hidden='true' className='h-3 w-3' strokeWidth={3} />
                  : isPicked && <Check aria-hidden='true' className='h-3 w-3' strokeWidth={3} />}
                {v.value}
              </span>
            </span>
          );
          if (!interactive) return <span key={v.value}>{cell}</span>;
          return (
            <button
              key={v.value}
              type='button'
              role='radio'
              aria-checked={isPicked}
              // An unlabelled point is named by the number printed on it, and nothing else. It was
              // "Value 3", which spends the name on a word that says nothing a listener did not
              // already know from the radio group — and where 3 SITS is now declared below rather
              // than smuggled into the name.
              aria-label={labelIsFormula ? undefined : plainLabel ? t('blocks.scale.valueWithLabel', { value: v.value, label: plainLabel }) : t('blocks.scale.valueLabel', { number: v.value })}
              /**
               * Where this point sits on the scale, stated rather than left to be counted.
               *
               * Readers do announce position inside a radiogroup, but only when they can work the
               * set out from the DOM — and these buttons are grid cells whose values need not
               * start at 1 or run consecutively, so an inferred "3 of 5" could disagree with the
               * number printed on the button. Declaring both makes the two agree, and it is the
               * one fact an unlabelled midpoint has: knowing 3 is the middle of 5 is what makes
               * an unlabelled scale usable at all.
               */
              aria-posinset={i + 1}
              aria-setsize={shown.length}
              tabIndex={i === focusIdx ? 0 : -1}
              ref={el => { cellRefs.current[v.value] = el; }}
              onClick={() => onSelect?.(v.value)}
              onKeyDown={e => handleKeyDown(e, i)}
              className='transition-shadow'
            >
              {cell}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Point row (the editor's answer-setup list) ────────────────────────────────

/**
 * One point of the scale, as authored: `[N] [label] [role cycler] [trash on the last only]`.
 *
 * Deliberately MCQ's option-row shape — text field, role toggle, delete — because that is the house
 * pattern for exactly this, and it is what merging the old separate column-header list into the
 * per-value role strip finally allows. Two lists became one.
 *
 * No drag handle and no mid-list delete. A point's position IS its stored value, so reordering or
 * removing from the middle would renumber everything below and silently re-point every targeted
 * trigger and branch role keyed to those values. A 1..N scale only ever changes length: adding
 * appends N+1, and only the last point can be removed. Relabelling after a 5→7 change is a
 * tolerable one-off; silently re-pointing a teacher's feedback is not.
 */
function ScalePointRow({
  point, isLast, canDelete, armed, setArmed, onLabelChange, roleLocked, onRoleClick, onRemove,
  labelEditorsRef, activeEditorRef, setActiveEditor,
}: {
  point: ScaleValue;
  isLast: boolean;
  canDelete: boolean;
  armed: boolean;
  setArmed: (v: boolean) => void;
  onLabelChange: (value: number, label: string) => void;
  roleLocked: boolean;
  onRoleClick: (value: number) => void;
  onRemove: (value: number) => void;
  /** Keyed by value, not index — the axis renumbers (see handleFirstValueChange) and an index would
   * hand the toolbar the wrong editor the moment it does. */
  labelEditorsRef: React.MutableRefObject<Map<number, Editor>>;
  activeEditorRef: React.MutableRefObject<Editor | null>;
  setActiveEditor: (e: Editor | null) => void;
}) {
  /* Warns when this row is the only thing choosing a version somewhere later —
     see `useDrivenRowDependents`. Undefined for the overwhelming majority. */
  const strandWarning = useDrivenRowDeleteWarning(String(point.value));
  /* See `useArmSettle`: the warning below is what moves the button. */
  const armSettled = useArmSettle(armed);
  const { t } = useTranslation();
  const roleLabel =
    point.role === 'correct' ? t('blocks.scale.markedCorrectCycle', { value: point.value })
      : point.role === 'incorrect' ? t('blocks.scale.markedIncorrectCycle', { value: point.value })
      : t('blocks.scale.markRole', { value: point.value });
  const Icon = point.role === 'correct' ? Check : point.role === 'incorrect' ? X : Minus;

  return (
    <div className={`flex items-center gap-2 rounded-lg p-2 ${armed ? 'border border-ws-warning-fg bg-ws-warning-bg/50' : ''}`}>
      <span aria-hidden='true' className='w-5 shrink-0 text-center text-xs font-semibold tabular-nums text-ws-muted'>
        {point.value}
      </span>
      {/* Number, then role, then label — the two fixed-width controls lead so every row's label
          field starts at the same x, instead of the role button drifting with label length. */}
      <button
        type='button'
        onClick={() => onRoleClick(point.value)}
        disabled={roleLocked}
        aria-label={roleLabel}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          point.role === 'correct' ? 'border-ws-primary bg-ws-primary text-ws-btn-on-primary' :
          point.role === 'incorrect' ? 'border-ws-border bg-ws-muted/20 text-ws-muted' :
          'border-ws-border bg-ws-btn text-ws-muted'
        }`}
      >
        <Icon aria-hidden='true' className='h-4 w-4' strokeWidth={3} />
      </button>
      {/* Rich, like every other block's student-facing option text — a science scale has to be able
          to write cm³, CO₂ or Ca²⁺, which a plain input cannot hold from the symbols picker. Wired
          to the shared toolbar the same way Dropdown's option rows are: hideToolbar here, and focus
          hands this editor to the one toolbar at the top of the panel. */}
      {/* Naming only, no box of its own: RichTextEditor's contenteditable already carries
          `rounded border border-ws-border bg-ws-btn`, so styling this wrapper too drew a second
          border around the first. It stays as a role='group' purely to keep the per-point
          accessible name the plain input used to have — the editor's own aria-label is the
          placeholder, identical on all 15 rows, which would lose "point 3". */}
      <div
        role='group'
        aria-label={t('blocks.scale.pointLabelAria', { number: point.value })}
        className='min-w-0 flex-1'
      >
        <RichTextEditor
          content={point.label}
          onChange={html => onLabelChange(point.value, html)}
          placeholder={t('blocks.scale.pointLabelPlaceholder')}
          editorClassName='min-h-6'
          hideToolbar
          onEditorReady={e => { labelEditorsRef.current.set(point.value, e); }}
          onEditorFocus={() => {
            const e = labelEditorsRef.current.get(point.value);
            if (e) { activeEditorRef.current = e; setActiveEditor(e); }
          }}
        />
      </div>
      {/* Only the last point is removable, and the control keeps its space on every other row so the
          list does not shift as points are added — same reserved-space rule as every other two-click
          armed delete in the app. */}
      {/* OUTSIDE the reserved box below, which is `w-9` and would clip it. */}
      {armed && strandWarning && <StrandWarning text={strandWarning} />}
      <span className='w-9 shrink-0'>
        {isLast && (
          <IconButton
            type='button'
            variant='destructive'
            size='sm'
            isArmed={armed}
            disabled={!canDelete}
            /*
               SAID TWICE, ON PURPOSE: in the armed label for a reader, and in the warning
               beside the button for everyone else. It used to be the label alone, which made
               it invisible — see `StrandWarning` for why that was chosen and why it was
               wrong. `title` stays for a pointer resting on the icon itself. */
            aria-label={armed
              ? [t('blocks.scale.confirmRemovePoint'), strandWarning].filter(Boolean).join('. ')
              : t('blocks.scale.removePoint')}
            title={armed ? strandWarning : undefined}
            onClick={() => { if (armed) { onRemove(point.value); setArmed(false); } else setArmed(true); }}
            onPointerLeave={e => { if (e.pointerType !== 'touch' && armed && armSettled()) setArmed(false); }}
            onBlur={() => { if (armed) setArmed(false); }}
            onKeyDown={e => { if (e.key === 'Escape' && armed) { e.stopPropagation(); setArmed(false); } }}
          >
            <Trash2 className='h-4 w-4' />
          </IconButton>
        )}
      </span>
    </div>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI (flat list across every row's non-correct values) ─────────────


// One flat dropdown per feedback state — catch-alls first, targeted points below (see
// buildScaleTriggerOptions) — matching MCQ's own single-dropdown trigger picker shape. No range
// mode, no mode-switching: with "All Incorrect"/"All Neutral" catch-alls available directly, an
// arbitrary numeric span never added anything a role-based catch-all didn't already cover.
function ScaleTriggerEditor({ trigger, values, states, excludeStateId, onChange }: {
  trigger: string;
  values: ScaleValue[];
  states: FeedbackState[];
  excludeStateId: string;
  onChange: (trigger: string) => void;
}) {
  const { t } = useTranslation();
  const options = buildScaleTriggerOptions(values, states, excludeStateId, t);
  return <TriggerDropdown value={trigger} options={options} onChange={onChange} />;
}

function ScaleTriggerChip({ option }: { option: TriggerOption }) {
  const chipColor =
    chipToneClass(option.evaluation ?? 'neutral');
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {option.evaluation === 'incorrect' && <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {option.evaluation === 'correct' && <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {(option.evaluation === 'neutral' || !option.evaluation) && <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
      <ChipDivider />
      <span className={`${TRIGGER_CHIP_TEXT_CLASS} font-mono`}>{option.label}</span>
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const scaleTestDataCache: IssueCache<ScaleTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft -- see keyword-block. Preview's chips describe the SUBMITTED
 * answer; Behind the Scenes wants what would fire now, and the two part company the moment
 * someone changes their answer after submitting.
 */
const scaleDraftChipsCache: IssueCache<ScaleTestData> = new WeakMap();
const scaleIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

export default function ScaleBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: ScaleBlockProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level copy is vestigial once a bank exists. */
  const initial = useRef(applyScaleVersion(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));
  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);

  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);
  const [editedReverseOrder, setReverseOrder] = useState(initial.current.reverseOrder);
  const [editedValues, setValues] = useState<ScaleValue[]>(initial.current.values);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalRoleBranch[] | undefined>(initial.current.conditionalBranches);
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);
  const branchPrevButtonRef = useRef<HTMLButtonElement>(null);
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle — the ref is just the fast
   * path; the block is where it actually lives. Dropped on close, and that is what makes the removal
   * permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<ScaleBlockData['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, reverseOrder: editedReverseOrder, values: editedValues,
    gradingMode: editedGradingMode, conditionalEquation: editedConditionalEquation,
    conditionalBranches: editedConditionalBranches, angleMode: editedAngleMode,
  };
  const restored = useRestoredData<ScaleBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyScaleVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...applyScaleVersion(initData(block.data), editorVersion), ...edited } as ScaleBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: ScaleBlockData) {
      setQuestionHtml(next.question);
      setReverseOrder(next.reverseOrder);
      setValues(next.values);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setAngleMode(next.angleMode ?? 'degrees');
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyScaleVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyScaleVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const reverseOrder = isEditing ? editedReverseOrder : restored.data.reverseOrder;
  const values = isEditing ? editedValues : restored.data.values;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addScaleVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addScaleVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * EVERY field write goes through here: `onUpdate` REPLACES `block.data`, so a write that builds
   * its object field by field deletes everything it forgot to name. Taking a PARTIAL is the point.
   */
  const emitFields = (next: Partial<ScaleBlockData>) => {
    const base = initData(block.data);
    emit(writeScaleVersion(base, editorVersion, { ...base, ...next } as ScaleBlockData) as unknown as Record<string, unknown>);
  };

  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read an issue list built from `block.data`, the editor panel reads one
  // built from live state, and the two are often rendered by different instances of this
  // component — so each must be gated by the settling of the value it was computed from.
  // No `answers` here: this block's answers are not equations, only its branching selector is.
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation },
    { equation: initData(block.data).conditionalEquation },
  );

  const currentDataRef = useRef({ questionHtml, values, reverseOrder, gradingMode, conditionalEquation, conditionalBranches, angleMode });
  currentDataRef.current = { questionHtml, values, reverseOrder, gradingMode, conditionalEquation, conditionalBranches, angleMode };

  // Branches are authored exclusively in graded mode, so this is gated on the mode and not merely on
  // `conditionalBranches` existing — switching to Participation deliberately keeps the data.
  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;

  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block: a task whose blocks all read {n}
  // has to agree about n. Block-level testing keeps its own private roll plus a reroll button, which
  // is the whole point of it — a teacher steps through combinations for the block being tested.
  const taskPreviewRoll = useTaskPreviewRoll();
  const rolledValues = isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues;

  // Two snapshots, exactly as FITB: `testValues` is what is picked right now, `submittedValues` is
  // what was last graded. Continuous mode is just "submit on every change" — keeping them separate
  // is what lets Continuous be turned off without the already-shown result vanishing.
  /**
   * What the first-number field is showing while it is being typed in. A committed-only value cannot
   * be typed into: clearing it to type "-3" or "10" passes through "" and "-", neither of which is a
   * number, so the field would fight every keystroke. Null means "not being edited, show the data".
   */
  const [firstValueDraft, setFirstValueDraft] = useState<string | null>(null);
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   */
  const prior = usePriorAnswer<number | null>();
  const [testValue, setTestValue] = useState<number | null>(prior ?? null);
  const [submittedValue, setSubmittedValue] = useState<number | null>(prior ?? null);
  const [hasSubmitted, setHasSubmitted] = useState(prior !== undefined);
  // The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
  // of the student's typing rather than of what they chose to hand in.
  useReportAnswer(hasSubmitted ? submittedValue : undefined);
  const [continuous, setContinuous] = useState(true);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');

  // Deselecting the card resets the test back to untouched, so the next teacher to open it sees a
  // clean surface rather than someone else's half-finished answer. Selecting it rolls fresh values.
  useEffect(() => {
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      //
      // Scale was the one block of six that never got this guard — Dropdown, FITB, Keyword, MCQ
      // and Ordering all carry it verbatim. Reported as Scale alone coming back empty from a
      // revisit that restored everything else on the same task.
      } else if (!isPreview) {
      setTestValue(null);
      setSubmittedValue(null);
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);

  function handleTestSelect(value: number) {
    // Re-picking the same point clears it — the only route back to "unanswered", since radio groups
    // have no native deselect and the `unanswered` state is otherwise untestable once touched.
    const next = testValue === value ? null : value;
    setTestValue(next);
    // `&& !isPreview` — continuous testing is a TEACHER affordance (the toggle lives in the test
    // bar), and Preview reuses this same change handler for the student's input. Without the
    // guard, typing an answer graded it instantly: feedback appeared before Submit was ever
    // clicked, which is not what a student sees. Preview submits through previewSubmitSignal
    // and nothing else.
    if (continuous && !isPreview) { setSubmittedValue(next); setHasSubmitted(true); }
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedValue(testValue); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedValue(testValue);
    setHasSubmitted(true);
  }
  function handleTestReset() {
    setTestValue(null);
    setSubmittedValue(null);
    setHasSubmitted(false);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
  }

  /**
   * Branch-UI gating: a chip in the QUESTION, and deliberately NOT one in a row label.
   *
   * Branching answers "which value is correct this time", and that is a property of the scenario the
   * question poses. A chip in a label changes what a scale point is *called* — "up to {n} cm" — which
   * is display text, not a different question with a different answer. Every other branching block
   * (MCQ, Dropdown, Keyword, FITB, Ordering) gates on its question alone, for the same reason.
   *
   * Reroll gating is a separate question and DOES still count labels — a label chip needs rolling to
   * be seen. That one lives in `computeScaleTestData`, via `scaleHasChips`.
   *
   * Reads in-flight editor state rather than saved block data, so the toggle appears the instant a
   * chip is typed and the branch data clears the instant the last one is deleted.
   */
  const questionHasChips = htmlHasVariableChips(questionHtml);

  // Preview's single bottom Submit (see task-content.tsx's doc comment on the signal) — reacts to
  // the signal CHANGING, not to a per-block button, and reuses the same state the live test widget
  // uses, so Preview is a different trigger for one evaluation path rather than a second one.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedValue(testValue);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  // `updateFeedbackState`/`deleteFeedbackState`/`isDupTitle` are deliberately not taken from the
  // hook: every feedback read and write goes through the branch-aware indirection below, so using
  // the hook's block-level versions anywhere would silently edit the wrong feedback set while a
  // branch is selected.
  const { feedback, setFeedback, resyncFeedback, getTitle } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
    },
    // No `incomplete_answer` extra: it meant "some sub-items answered, not all", which a single
    // axis cannot be in, and `evaluateScale` has no rung that can fire it. Declaring it here still
    // CREATED and persisted the state in every saved block even though the panel stopped rendering
    // a slot for it — invisible dead data that Flow and analysis would still enumerate. Dropping it
    // from the declaration also cleans existing blocks on load, since normalizeFeedback keeps only
    // the triggers a block declares.
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const d = currentDataRef.current;
      emitFields({
        question: d.questionHtml, values: d.values, reverseOrder: d.reverseOrder,
        gradingMode: d.gradingMode, feedback: nextFeedback,
      });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);

  // Which point's delete is armed (two-click), by value. Only the last point is deletable.
  const [armedPoint, setArmedPoint] = useState<number | null>(null);
  // One TipTap instance per point label, so focus can hand the right one to the shared toolbar.
  const labelEditorsRef = useRef<Map<number, Editor>>(new Map());
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Every plain <input> in this block focuses through here: without it the shared toolbar stays
  // pointed at whichever RichTextEditor was last active (the Question), so pressing Bold with the
  // cursor in a numeric field silently formats the question instead. The toolbar also ghosts itself
  // once this clears activeEditor — input-block-frame.tsx already drives `toolbarVisible` from it,
  // so disconnecting is all that's needed to get the greyed-out state too.
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const questionEditorRef = useRef<Editor | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function push(
    qHtml = questionHtml,
    vals = values,
    mode = gradingMode,
    fdbk = feedback,
    rev = reverseOrder,
    cb = conditionalBranches,
    eq = conditionalEquation,
    angMode = angleMode,
  ) {
    // The retained draft rides along on every ordinary save, so editing anything else cannot drop
    // it. Deliberately not a parameter: a defaulted parameter cannot express "clear this", which is
    // the trap `pushBranchState` exists to avoid.
    emitFields({ question: qHtml, values: vals, reverseOrder: rev, gradingMode: mode, feedback: fdbk, conditionalEquation: eq, conditionalBranches: cb, conditionalBranchesDraft: savedConditionalDraftRef.current, angleMode: angMode });
  }

  /**
   * Every write that changes the branch state itself. It cannot go through `push`: a JS default
   * parameter fires on `undefined`, so `push(..., undefined, undefined)` restores
   * `cb = conditionalBranches` instead of clearing it — the trap that made "turn branching off"
   * silently never stick, leaving the canvas card grading against branches the editor said were off.
   * Passing all three explicitly makes each transition say exactly what it means.
   */
  function pushBranchState(
    branches: ConditionalRoleBranch[] | undefined,
    equation: string | undefined,
    draft: ScaleBlockData['conditionalBranchesDraft'],
  ) {
    emitFields({
      question: questionHtml, values, reverseOrder, gradingMode, feedback,
      conditionalEquation: equation, conditionalBranches: branches,
      conditionalBranchesDraft: draft, angleMode,
    });
  }

  const scenario = useScenarioFollower<ScenarioRoleBranch<{ states: FeedbackState[] }>>({
    block, blocks, gradingMode, feedback, isEditing, isPreview,
    storedData: initData(block.data) as never,
    restoredRows: restored.data.scenarioBranches,
    initialRows: initial.current.scenarioBranches,
    emit: data => emit(versionStructure(block.data, data)),
    applyVersion: applyScaleVersion,
    emitFields: next => emitFields(next as Partial<ScaleBlockData>),
    /* Keyed by `buildScaleValueTrigger(value)`, exactly as the numeric branch is — a row and a
       branch differ in what SELECTS them, never in what they carry. */
    rowTemplate: () => ({
      roles: Object.fromEntries(values.map(v => [buildScaleValueTrigger(v.value), v.role])),
      feedback,
    }),
    rowFromVersion: v => ({
      roles: (v as unknown as { roles: Record<string, ScaleValue['role']> }).roles,
      feedback: (v as unknown as { feedback: { states: FeedbackState[] } }).feedback,
    }),
    numeric: {
      read: () => ({ equation: conditionalEquation, branches: conditionalBranches }),
      write: (eq, br) => {
        setConditionalEquation(eq as string | undefined);
        setConditionalBranches(br as ConditionalRoleBranch[] | undefined);
      },
    },
  });

  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write of feedback CONTENT goes through these rather than `feedback`/`setFeedback`
  // directly, so the slot-building JSX below works unchanged whether branching is on or off. Same
  // layer Dropdown, Keyword and FITB all carry.
  /* WHICHEVER setup the panel is editing — a numeric branch and a scenario row differ only in
     what SELECTS them, and every read below this line is one path. */
  const activeSetup = scenario.activeScenario ?? activeBranch;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (scenario.activeScenario) {
      scenario.writeActiveRow({ feedback: { states: nextStates } });
    } else if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push(questionHtml, values, gradingMode, feedback, reverseOrder, nextBranches);
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push(questionHtml, values, gradingMode, next);
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  /**
   * Rows with the active branch's role map applied — what the setup grid shows and clicks against
   * while a branch is selected. Falls back to the plain bounded rows when branching is off.
   */
  const effectiveValuesForActive: ScaleValue[] = rolesForMode(gradingMode, activeBranch
    ? values.map(v => ({ ...v, role: activeBranch.roles[buildScaleValueTrigger(v.value)] ?? v.role }))
    : values);

  function isTargetedStaleForActive(s: FeedbackState): boolean {
    if (s.type !== 'targeted') return false;
    /**
     * A blank only fires in OPTIONAL. Graded and survey catch it as `unanswered` BEFORE anything
     * targeted runs, so the card could never fire there — it sat in the panel looking authored
     * and resolving to nothing.
     *
     * Hidden rather than deleted, like every other stale targeted state: switching back to
     * optional brings it and its text back, which is the same promise targeted feedback makes
     * across every other mode change.
     */

    return isTriggerFullyCorrect(s.trigger, effectiveValuesForActive);
  }

  /**
   * Renumbers the whole axis so it starts at `next`, for the scales that are not 1-based: pH runs
   * 0-14, Beaufort 0-12, Mercalli I-XII. Storing 1-15 for a pH scale would put a number in the
   * export that the science does not use.
   *
   * A point's *value* is its identity here — targeted feedback, branch role maps and every branch's
   * own feedback set all key on `value_N` — so renumbering has to carry all of them with it or the
   * references silently detach from the points they were written for. Shifting rather than
   * rewriting is what keeps that safe: every value moves by the same delta, so the relationships
   * between them are untouched.
   */
  function handleFirstValueChange(next: number) {
    const delta = next - (values[0]?.value ?? 1);
    if (delta === 0) return;
    const shifted = values.map(v => ({ ...v, value: v.value + delta }));
    const shiftStates = (states: FeedbackState[]) => states.map(st => {
      if (st.type !== 'targeted') return st;
      const value = parseScaleValueTrigger(st.trigger);
      // A trigger this doesn't recognise is left exactly as it is. Nothing writes one today, and
      // silently rewriting a string whose shape we don't know is how debris becomes corruption.
      return value === null ? st : { ...st, trigger: buildScaleValueTrigger(value + delta) };
    });
    const nextFeedback = { states: resyncScaleTargetedTitles(shifted, shiftStates(feedback.states), t) };
    const nextBranches = conditionalBranches?.map(b => ({
      ...b,
      roles: Object.fromEntries(Object.entries(b.roles).map(([key, role]) => {
        const value = parseScaleValueTrigger(key);
        return [value === null ? key : buildScaleValueTrigger(value + delta), role];
      })),
      feedback: { states: resyncScaleTargetedTitles(shifted, shiftStates(b.feedback.states), t) },
    }));
    setValues(shifted);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    // The live test is showing a point that just got a new number. Move the selection with it rather
    // than dropping it, so renumbering mid-test does not silently clear the answer under the teacher.
    setTestValue(v => (v === null ? v : v + delta));
    setSubmittedValue(v => (v === null ? v : v + delta));
    push(questionHtml, shifted, gradingMode, nextFeedback, reverseOrder, nextBranches);
  }

  function handleReverseOrder(val: boolean) {
    setReverseOrder(val);
    push(questionHtml, values, gradingMode, feedback, val);
  }

  function handleGradingMode(mode: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });
    setGradingMode(mode);
    push(questionHtml, values, mode, { states: nextFeedbackStates });
  }

  function handleQuestionChange(html: string) {
    setQuestionHtml(html);
    push(html);
  }

  function handleLabelChange(value: number, label: string) {
    const next = values.map(v => v.value === value ? { ...v, label } : v);
    // A label change moves the trigger text with it, so every targeted title has to follow — the
    // title IS the derived label, never independently editable text that could drift.
    const nextFeedback = { states: resyncScaleTargetedTitles(next, feedback.states, t) };
    setValues(next);
    setFeedback(nextFeedback);
    push(questionHtml, next, gradingMode, nextFeedback);
  }

  function handleValueRoleClick(value: number) {
    // Starts at 'incorrect' (nothing correct until configured), so the first click on a fresh point
    // goes straight to 'correct' — the teacher's most common first action.
    const CYCLE: ScaleValue['role'][] = ['incorrect', 'correct', 'neutral'];
    const next = values.map(v => v.value === value
      ? { ...v, role: CYCLE[(CYCLE.indexOf(v.role) + 1) % CYCLE.length]! }
      : v);
    // Every targeted feedback's evaluation mirrors its point's (or role trigger's own) role — keep
    // them in sync whenever a role changes rather than letting the two silently drift apart.
    const nextFeedback = { states: resyncScaleTargetedEvaluations(next, feedback.states) };
    setValues(next);
    setFeedback(nextFeedback);
    push(questionHtml, next, gradingMode, nextFeedback);
  }

  /**
   * Appends point N+1. End-only by design: a point's position IS its stored value, so inserting in
   * the middle would renumber everything below it and silently re-point every targeted trigger and
   * branch role keyed to those values.
   */
  function addPoint() {
    if (values.length >= SCALE_MAX_VALUES) return;
    const role: ScaleValue['role'] = gradingMode === 'graded' ? 'incorrect' : 'neutral';
    // Last value + 1, not length + 1: the axis does not have to start at 1 (see
    // handleFirstValueChange), and length-based numbering would collide with an existing point the
    // moment it doesn't.
    const added: ScaleValue = { value: (values[values.length - 1]?.value ?? 0) + 1, label: '', role };
    const next = [...values, added];
    // Every branch needs an opinion about the new point, or `resolveEffectiveValues` leaves it at
    // its static role and the new point gets graded by the key the branches were meant to replace.
    const nextBranches = conditionalBranches?.map(b => ({
      ...b,
      roles: { ...b.roles, [buildScaleValueTrigger(added.value)]: added.role },
    }));
    setValues(next);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, next, gradingMode, feedback, reverseOrder, nextBranches);
  }

  /** Removes the LAST point only — see `addPoint` for why the middle is off limits. */
  function removeLastPoint() {
    if (values.length <= SCALE_MIN_VALUES) return;
    const removed = values[values.length - 1]!;
    const next = values.slice(0, -1);
    // Feedback pinned to the removed point can never fire again, so it goes with it. A role
    // catch-all survives: it names a role, not a position, and re-resolves against what is left.
    const survivingStates = feedback.states.filter(st =>
      !(st.type === 'targeted' && parseScaleValueTrigger(st.trigger) === removed.value));
    const nextFeedback = { states: resyncScaleTargetedTitles(next, survivingStates, t) };
    const nextBranches = conditionalBranches?.map(b => {
      const roles = { ...b.roles };
      delete roles[buildScaleValueTrigger(removed.value)];
      return { ...b, roles };
    });
    setValues(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, next, gradingMode, nextFeedback, reverseOrder, nextBranches);
  }

  /** The branch-scoped twin of `handleValueRoleClick` — same cycle, writing into the branch's role
   * map instead of the point's own static role. */
  /** The three-way cycle, shared by the numeric branch and the scenario row so the two cannot
   *  disagree about what pressing the same point does. */
  function rolesAfterPointClick(roles: Record<string, ScaleValue['role']>, value: number) {
    const CYCLE: ScaleValue['role'][] = ['incorrect', 'correct', 'neutral'];
    const key = buildScaleValueTrigger(value);
    const current = roles[key] ?? values.find(v => v.value === value)?.role ?? 'incorrect';
    return { ...roles, [key]: CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length]! };
  }

  function handleBranchValueRoleClick(branchId: string, value: number) {
    const next = (conditionalBranches ?? []).map(b =>
      (b.id === branchId ? { ...b, roles: rolesAfterPointClick(b.roles, value) } : b));
    setConditionalBranches(next);
    push(questionHtml, values, gradingMode, feedback, reverseOrder, next);
  }

  /** The same act keyed by variant. Deciding a row's roles IS reviewing it. */
  function handleScenarioValueRoleClick(value: number) {
    const row = scenario.activeScenario;
    if (row) scenario.writeActiveRow({ roles: rolesAfterPointClick(row.roles, value) } as never);
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    // effectiveValuesForActive, not values — while a branch is selected, "which points are still
    // targetable" is a question about THAT branch's answer key, not the static one.

    // Outside graded no rating is correct, so every position can carry a targeted state.
    const target = effectiveValuesForActive.find(v =>
      (gradingMode !== 'graded' || v.role !== 'correct') && !valueCoveredByTrigger(activeFeedbackStates, v.value));
    if (!target) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const trigger = buildScaleValueTrigger(target.value);
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: triggerSummary(trigger, values, t)?.label ?? trigger,
      trigger,
      content: '', evaluation: target.role === 'neutral' ? 'neutral' : 'incorrect',
    };
    // setActiveFeedbackStates already persists — writing into a branch's own feedback set when one
    // is active, or the block's when not — so there is no second push here.
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }

  // ── Conditional branches ────────────────────────────────────────────────────

  /** Fresh ids so updating branch 1's Correct card can never also match the top-level Correct card
   * it was cloned from. Mirrors every other branching block's helper of the same name. */
  function cloneFeedbackWithFreshIds(source: ScaleBlockData['feedback']): ScaleBlockData['feedback'] {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Seeds each branch after the first from the one that was active when Add was clicked. Structure
   * carries over; content resets to blank if the teacher had customized it, so a message written for
   * one branch is never silently duplicated into every branch forked afterwards. */
  function seedBranchFeedback(source: ScaleBlockData['feedback']): ScaleBlockData['feedback'] {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching exists only in graded mode, so a branch's positive slot is always `correct`.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  /** Every active row+value key, seeded from the roles currently in force. */
  function currentRoleMap(source: ScaleValue[]): Record<string, ScaleValue['role']> {
    return Object.fromEntries(source.map(v => [buildScaleValueTrigger(v.value), v.role] as const));
  }

  function makeConditionalBranch(seedFrom: ScaleBlockData['feedback'], mode: 'migrate' | 'fork'): ConditionalRoleBranch {
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      thresholdHigh: '',
      roles: currentRoleMap(values),
      feedback: mode === 'migrate' ? cloneFeedbackWithFreshIds(seedFrom) : seedBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      pushBranchState(branches, equation, undefined);
    } else {
      // Banked under its own key and out of `conditionalBranches` at once. Recovery from an
      // accidental untick needs the set to survive, but the canvas card grades off the saved block,
      // so leaving it in place would have the card resolving a branch the editor says is off. A
      // separate key gives both — and unlike a memory-only draft, it survives a reload.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      pushBranchState(undefined, undefined, draft);
    }
  }

  /**
   * Closing the editor is what makes a switched-off branch set unrecoverable: the in-memory draft is
   * dropped and undo becomes the only way back. Called from `handleDone`; switching to another block
   * unmounts this instance, which discards the ref with it, so that route needs no extra handling.
   */
  function discardBranchDraft() {
    const had = savedConditionalDraftRef.current !== undefined;
    savedConditionalDraftRef.current = undefined;
    // Only when branching is actually off — closing with it ON must not touch the live set.
    if (had && conditionalBranches === undefined) pushBranchState(undefined, undefined, undefined);
  }

  // A variable chip somewhere in the block is what makes branch-based correctness mean anything.
  // Hiding the UI when the last chip goes is not enough on its own: the branches would still sit
  // there, still consulted by evaluateScale. Runs every render rather than tracking the HTML, same
  // as the identical effect on Dropdown/Keyword/FITB.
  useEffect(() => {
    if (!isEditing) return;
    if (!questionHasChips && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push(questionHtml, values, gradingMode, feedback, reverseOrder, conditionalBranches, val);
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push(questionHtml, values, gradingMode, feedback, reverseOrder, next);
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push(questionHtml, values, gradingMode, feedback, reverseOrder, next);
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalRoleBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push(questionHtml, values, gradingMode, feedback, reverseOrder, next);
  }

  function handleDone() {
    push();
    discardBranchDraft();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Feedback branch scroller ────────────────────────────────────────────────
  // The same `activeBranchIndex` the setup grid uses, exposed again here so switching branches from
  // the feedback panel can never leave the two views looking at different branches.
  // Graded-only, like every other branching surface (activeBranch, showConditionalUI): branches are
  // read straight from persisted data, so a block authored with branches and later switched to
  // Participation/Optional kept showing this scroller above states those branches can never reach.
  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const d = buildNormalizedScaleData(initData(block.data), t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const testData = memoizedByKey(
      /* The DRAWN VERSION is part of the key, or this cache answers for whichever version it saw
         first and the content never moves. */
      scaleTestDataCache, block.data,
      [variables, t, rolledValues, submittedValue, hasSubmitted, taskPreviewRoll?.[versionRollKey(block.id)],
       /* The LEADER's draw belongs in the key too: the scenario decides the answer key. */
       drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)],
      // The ONE task roll both blocks read, so the leader's prose and this block's answer key
      // describe the same scenario (§3.1).
      () => computeScaleTestData(d, variables, t, rolledValues, submittedValue, hasSubmitted,
        drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)),
    );
    // The per-block Submit that used to sit here was permanently `aria-disabled` and wired to
    // nothing — Preview has had one page-level Submit since `previewSubmitSignal` landed, so a
    // second dead one per block was pure noise.
    /* The same derivation asked about the DRAFT, reported and never rendered. */
    const draftData = memoizedByKey(
      scaleDraftChipsCache, block.data,
      [variables, t, rolledValues, testValue, taskPreviewRoll?.[versionRollKey(block.id)]],
      () => computeScaleTestData(d, variables, t, rolledValues, testValue, true,
        drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)),
    );
    return (
      <ScaleAnswerDisplay scenario={scenario.ghostedScenario} d={d} selected={testValue} onSelect={handleTestSelect} rolledValues={rolledValues}>
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftData.testChips} states={draftData.testResult} />
        {renderScaleFiredFeedback(testData.testResult)}
      </ScaleAnswerDisplay>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedScaleData(initData(block.data), t, shownVersionId);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const scaleAriaLabel = d.question
      ? t('blocks.scale.editAriaBaseWithQuestion', { question: stripHtmlForLabel(d.question).slice(0, 80) })
      : t('blocks.scale.editAriaBase');

    // One source. This bar used to hand-roll the same two conditions `scaleIssues` computes,
    // with its own icons and a mutually-exclusive if/else — so the card and the editor's warning bar
    // were one added check away from disagreeing about what is wrong with the same block. Now the
    // bar, the editor, and the test panel's `notices` all read this one list.
    const issues = settleStored(memoizedByKey(scaleIssuesCache, block.data, [variables, t, shownVersionId], () => scaleIssues(d, issueCtx)));
    const issueBar = collapsedIssueBar(issues);
    // The bar always says something, matching every audited block: the top issue when there is one,
    // otherwise the mode (Participation/Optional) or the answer key itself. Scale used to render
    // NOTHING once it was configured correctly, so a finished block's card was the only one in the
    // set with no answer summary at all. The key comes from `scaleCorrectChipLabel`, the same
    // derivation Flow uses, so the card and Flow state the answer identically.
    const barNode = (() => {
      if (issueBar) return issueBar;
      // The correct-answer bar exists for answers the DISPLAY cannot show — a numerical accepted
      // range, a GPS radius, a compass arc. Scale's card already draws its whole axis with the key
      // revealed, so restating the answer underneath said the same thing twice, and said it worse:
      // `scaleCorrectChipLabel` returns a plain string, so a value label containing a variable chip
      // arrived here as bare text rather than a chip.
      //
      // Phone portrait is the exception. There the axis is the one thing that has to give up room,
      // so the bar stays as the confirmation that an answer IS set, without restating it.
      // Graded says ONE thing on phone portrait — "Correct Answer Set" — whether the answer is a
      // single value or resolved per branch. "Correct answer set by 3 conditions" described the
      // SETUP, which is the same restatement in a different costume: the branch count is already
      // in the editor, and the bar's only job here is to confirm an answer exists.
      const hasGradedAnswer = (d.conditionalBranches && d.conditionalBranches.length > 0)
        || !!scaleCorrectChipLabel(d, t);
      const summary = d.gradingMode !== 'graded'
        ? t(d.gradingMode === 'survey' ? 'blocks.scale.barSurvey' : 'blocks.scale.barOptional')
        : (isPhone && !isPhoneLandscape && hasGradedAnswer)
          ? t('blocks.answerBar.correctSet')
          : null;
      if (!summary) return null;
      return (
        <CollapsedBlockBar tone='success'>
          <span className='min-w-0 truncate'>{summary}</span>
        </CollapsedBlockBar>
      );
    })();

    const testData = isSelected ? memoizedByKey(
      // Keyed on the shown version too — see the preview branch's note.
      scaleTestDataCache, block.data,
      [variables, t, rolledValues, submittedValue, hasSubmitted, shownVersionId, scenario.shownScenarioId],
      /* On the CARD the scenario is whichever one the pager is on, never a draw. */
      () => computeScaleTestData(d, variables, t, rolledValues, submittedValue, hasSubmitted,
        scenario.activeScenario?.scenarioId),
    ) : null;

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<Scale className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.scale.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={scaleAriaLabel}
        answerBarSlot={() => barNode}
        testEvaluation={testData?.testResult ? (testData.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected && testData ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testData.testChips,
          onReset: handleTestReset,
          // Versions are rollable too: a banked block can be rerolled with no chips in it.
          onReroll: (testData.hasChips && testData.hasRollable) ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump to editing it.
          /* One row for everything this test resolved to. Shared with the phone bar and every
             other banked block — see `version-branch-notice.tsx`. */
          topNotices: (
            <VersionBranchNotice
              versions={scenario.testPager?.versions ?? shownVersions}
              activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
              onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
              noun={scenario.testPager?.noun ?? 'version'}
              editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={testData.matchedBranch && testData.matchedBranchIndex !== -1 ? {
                index: testData.matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: testData.matchedEquationValue,
                operator: testData.matchedBranch.operator,
                threshold: testData.matchedBranch.threshold,
                thresholdHigh: testData.matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (testData.matchedBranch && testData.matchedBranchIndex !== -1) requestBranchJump(block.id, testData.matchedBranchIndex);
                editShownVersion(testData.matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected && testData ? (
          <ScaleAnswerDisplay scenario={scenario.ghostedScenario} d={d} selected={testValue} onSelect={handleTestSelect} rolledValues={rolledValues}>
            {renderScaleFiredFeedback(testData.testResult)}
          </ScaleAnswerDisplay>
        ) : (
          <>
            <BlockQuestion html={d.question} spaced showEmptyNote />
            {/* Unselected, this stays the teacher-facing overview with the key revealed — the one
                place `revealAnswers` still belongs, since nobody is answering here. */}
            <ScaleWidgetPreview values={rolesForMode(d.gradingMode, d.values)} reverseOrder={d.reverseOrder} revealAnswers compact />
          </>
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  // Feedback is stale (suppressed, not deleted) once every value in its span is marked correct.
  // Judged against the ACTIVE branch's roles when one is selected: a value can be wrong in branch 1
  // and correct in branch 2, so staleness is a per-branch question, not a block-wide one.
  const visibleTargetedStates = targetedStates.filter(s => !isTargetedStaleForActive(s));

  // Outside graded every answer can carry a targeted state (nothing is correct, so nothing is
  // terminal), plus the blank in optional — see addTargetedState for why a blank is in this list.
  const canAddMore = effectiveValuesForActive.some(v =>
    (gradingMode !== 'graded' || v.role !== 'correct') && !valueCoveredByTrigger(targetedStates, v.value));

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, visibleTargetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={visibleTargetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {visibleTargetedStates.map(s => {
            // `effectiveValuesForActive`, not `values`: the former is through `rolesForMode`, so
            // outside graded the summary's own evaluation comes back neutral. Reading the raw
            // list made the COLLAPSED chip red while the open card beside it was neutral — the
            // same card describing itself two ways depending on whether it was shut.
            const summary = triggerSummary(s.trigger, effectiveValuesForActive, t);
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                title={s.title} content={s.content} evaluation={displayEvaluation(gradingMode, s.evaluation)}
 /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
    had collapsed sprang back open every time they flipped modes to compare. */
 collapseSignal={gradingMode}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                titleLocked
                evalLocked
                triggerZone={<ScaleTriggerEditor trigger={s.trigger} values={effectiveValuesForActive} states={activeFeedbackStates} excludeStateId={s.id} onChange={trigger => {
                  const nextSummary = triggerSummary(trigger, effectiveValuesForActive, t);
                  updateActiveFeedbackState(s.id, {
                    trigger,
                    title: nextSummary?.label ?? s.title,
                    evaluation: nextSummary?.evaluation ?? s.evaluation,
                  });
                }} />}
                triggerZoneSummary={summary ? <ScaleTriggerChip option={{ value: s.trigger, label: summary.label, evaluation: summary.evaluation }} /> : undefined}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => {}}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Over effectiveValuesForActive, not `values`: with a branch selected, "is there a correct
  // answer" is a question about THAT branch's role map, which is what the rest of the panel
  // (addTargetedState, canAddMore, staleness) has always asked. These summaries asked the static
  // list instead, so a branch that marked a different point correct still showed the static one.
  const panelReach = scaleReachability({ gradingMode, feedback: { states: activeFeedbackStates } }, effectiveValuesForActive);
  const panelCanFire = (trigger: string) => panelReach.states.find(x => x.trigger === trigger)?.reachable ?? false;
  const setupIncompleteForFeedback = gradingMode === 'graded' && !panelCanFire('correct');

  /** The chips a default state currently catches. One flat list now that there is one axis — the
   * per-row grouping and its "Row N:" prefix went with the rows. */
  // The same two lists the test legend draws — see `scaleChips`. What used to sit here was a second
  // copy of one condition, rendering literal tone classes and labelling its chips with the STRING
  // derivation, so a variable in a position's label arrived as bare `{name}`.
  const chipRow = (chips: ConditionChips) => (
    <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );
  const defaultCorrectInline = chipRow(scaleChips('correct', panelReach, effectiveValuesForActive, t));

  // Scale's points are a closed, fully-enumerable domain — there is no "any other value" a student
  // could submit outside the ones listed, so this lists every uncovered non-correct point rather
  // than appending a catch-all. One chip per point, never a collapsed range: a student picks exactly
  // one, so each is its own selectable answer, and the same rule governs the test legend (see
  // computeScaleTestData) so the two surfaces cannot disagree.
  const defaultIncorrectInline = chipRow(scaleChips('incorrect', panelReach, effectiveValuesForActive, t));

  /**
   * The catch-all LISTS what it still catches, exactly as Default Incorrect does in graded — a
   * generic pill left the teacher to work out which answers counted as "any", and that set
   * shrinks as targeted states claim them. Available here because this block's answer set is
   * CLOSED; the open-answer blocks say "any other X" instead and keep doing so.
   */
  const participationCatchAllInline = chipRow(catchAllChips(gradingMode, visibleTargetedStates.length > 0, t,
    () => scaleChips('incorrect', panelReach, effectiveValuesForActive, t, gradingMode)));
  const anyAnswerInline = participationCatchAllInline;
  const anyResponseInline = participationCatchAllInline;
  /**
   * An unreachable positive state shows the standard UnreachableChip in place of its trigger —
   * the treatment compass and numerical already use. Survey's Any Answer closes once every
   * option has a targeted state; optional's Any Response never does, a blank always reaching it.
   */
  const positiveUnreachable = gradingMode === 'graded'
    ? setupIncompleteForFeedback
    : !isReachable(panelReach, positiveTriggerForMode(gradingMode));
  const positiveTriggerZone = positiveUnreachable
    ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
    : gradingMode === 'survey' ? anyAnswerInline
    : gradingMode === 'optional' ? anyResponseInline
    : defaultCorrectInline;

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={positiveUnreachable}
        triggerZone={positiveTriggerZone}
        triggerZoneSummary={positiveTriggerZone}
        title={catchAllTitle(gradingMode, visibleTargetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /**
         * The tick goes the moment a target exists — the catch-all must not affirm an answer the
         * teacher ignored more strongly than one they wrote for. With no targets the block is
         * still a plain questionnaire and keeps it. Same rule `catchAllChips` applies to the chip
         * beside it, so the card and its trigger cannot disagree.
         */
        evaluation={gradingMode !== 'graded' && visibleTargetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={values.length === 0}
        triggerZone={defaultIncorrectInline}
        triggerZoneSummary={defaultIncorrectInline}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Phone Test tab ────────────────────────────────────────────────────────
  // Fed from `liveD` (the editor's own in-flight state), not `initData(block.data)`, so the Test tab
  // reflects an edit the instant it is made. Gated on `isPhone`, NOT on `phoneTestMode === 'test'`:
  // InputBlockFrame only renders its Edit/Test toggle once testSlot AND testBar are both truthy, so
  // gating on the mode would hide the toggle in Edit mode and leave no way to ever reach Test.
  /* Spread, so nothing has to be enumerated — but the scenario fields come from the hook's
     live rows rather than the stored copy, or a follower's live test grades against its own
     key instead of the row on screen. */
  const liveD: ScaleBlockData = { ...currentDataRef.current, question: questionHtml, values, feedback,
    followsBlockId: initData(block.data).followsBlockId, scenarioBranches: scenario.rows };
  // Built from live editor state, not block.data, so both the warning bar and the Test tab's
  // diagnostics react on the same keystroke as the field being typed into.
  const editorIssues = settleLive(scaleIssues(liveD, issueCtx));
  const phoneTest = isPhone ? computeScaleTestData(liveD, variables, t, rolledValues, submittedValue, hasSubmitted, scenario.activeScenario?.scenarioId) : null;
  const phoneTestSlot = phoneTest ? (
    <ScaleAnswerDisplay scenario={scenario.ghostedScenario} d={liveD} selected={testValue} onSelect={handleTestSelect} rolledValues={rolledValues}>
      {renderScaleFiredFeedback(phoneTest.testResult)}
    </ScaleAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: (phoneTest.hasChips && phoneTest.hasRollable) ? handleTestReroll : undefined,
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? shownVersions}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here: on a phone, Edit and Test are two tabs over ONE session. */
        onSelectVersion={scenario.testPager?.onSelect ?? selectVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      icon={<Scale className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.scale.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.scale.closeEditor')}
      onClose={handleDone}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          {/* First number decides where the scale STARTS, and the stored answers renumber with it,
              so it is an answer decision rather than a presentation one. Reversing the printed
              order is the only purely visual choice in this drawer. (Scale SIZE is in neither
              section: it is the length of the point list in the editor body.) */}
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            {/* Not every ordinal scale starts at 1: pH runs 0-14, Beaufort 0-12. The points renumber
                from here and the stored answers renumber with them, so an export reads in the units
                the science actually uses. */}
            <label className='flex items-center justify-between gap-3 text-sm text-ws-text'>
              <span>{t('blocks.scale.firstNumber')}</span>
              {/* An integer NumberField, not the `type='number'` this was — which was the only
                  native number input in the codebase and the reason for the wheel guard below it:
                  a focused type=number changes value on scroll, and scrolling past a field is not a
                  decision. Here that silently renumbered every point and every reference to them.
                  A text-based field cannot take the wheel as input at all, so the guard is gone with
                  the input rather than carried forward. SCALE_FIRST_MIN is -99, hence allowNegative. */}
              <NumberField
                grammar='integer'
                allowNegative
                value={firstValueDraft ?? String(values[0]?.value ?? 1)}
                onChange={v => {
                  setFirstValueDraft(v);
                  const parsed = Number(v);
                  // Commit only a whole number in range. A half-typed "-" or "" leaves the data alone
                  // and lives in the draft until it becomes one.
                  if (v.trim() !== '' && Number.isInteger(parsed)
                    && parsed >= SCALE_FIRST_MIN && parsed <= SCALE_FIRST_MAX) handleFirstValueChange(parsed);
                }}
                onBlur={() => setFirstValueDraft(null)}
                ariaLabel={t('blocks.scale.firstNumber')}
                className='w-20 tabular-nums'
              />
            </label>
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            <CheckboxRow fieldId='reverseOrder'
              checked={reverseOrder}
              onToggle={() => handleReverseOrder(!reverseOrder)}
              label={t('blocks.scale.reverseOrder')}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.scale.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: scenario.feedbackScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: visibleTargetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
          {/* No error ring on the question editor. Only the three option-based blocks (MCQ, Dropdown,
              Ordering) ring theirs; every audited block — Angle, Compass, GPS, Keyword, FITB — leaves
              it plain and lets the warning bar, which sits directly above the field, carry the
              message. A 2px ring on top of the editor's own border reads as much louder than the
              problem is. */}
          {/* A bank is a niche feature: in the heading it is available without being advertised,
              and matches ANSWER SETUP below so the editor gains a section rather than a widget. */}
          {/* The three-way offer, when linking meets an existing bank — §7.1. */}
          {scenario.remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!scenario.activeScenario}
            linkMenu={scenario.linkMenu}
            stimulus={scenario.editorScenario}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            /* First heading in the panel unless a scenario is above it. */
            divider={!!scenario.activeScenario}
            action={(
              <div className='flex items-center gap-2'>
              {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2):
                  versions are many stems each with one answer setup, a scenario is one stem
                  with many, and the two cannot both be the answer key. */}
              {!scenario.following && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addScaleVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeScaleVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the
                  control moves to — see `QuestionHeadingLink`. Written as
                  `!activeScenario` it knew about scenarios only, so a DRIVEN block
                  showed the link twice and the heading overflowed sideways. */}
              <QuestionHeadingLink
                following={!!scenario.activeScenario}
                linkMenu={scenario.linkMenu}
              />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={questionHtml}
            onChange={handleQuestionChange}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
          />

          <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

          {(() => {
          // A chip somewhere in the block (question or a row label) is what makes branch-based
          // correctness mean anything, so the whole toggle+equation+grid UI appears only then. A
          // separate effect clears the branch data itself when the last chip goes.
          /* FOLLOWING NO LONGER RULES BRANCHING OUT — when both are on the branching nests
             INSIDE the scenario row. See `use-nested-branching`. */
          const showConditionalUI = questionHasChips && gradingMode === 'graded';
          const isEnabled = conditionalBranches !== undefined;
          /* WHOSE branch set is on screen — the row's when following, the block's otherwise. */
          const { nesting, effEquation, effEnabled, effBranches, effIndex, effActiveBranch } =
            nestedBranching<ScaleBlockData['feedback']>({
              activeScenario: scenario.activeScenario,
              blockEquation: conditionalEquation,
              blockBranches: conditionalBranches ?? [],
              blockEnabled: isEnabled,
              activeBranchIndex,
              graded: gradingMode === 'graded',
            });
          const makeRow = (mode: SeedMode) => makeRowBranch<ScaleBlockData['feedback']>({
            mode,
            roles: (scenario.activeScenario as { roles?: Record<string, unknown> } | undefined)?.roles ?? {},
            feedback: (scenario.activeScenario as unknown as { feedback: ScaleBlockData['feedback'] }).feedback,
            cloneFeedback: cloneFeedbackWithFreshIds,
            seedFeedback: seedBranchFeedback,
          });
          /** One toggle, two scopes — the ROW's while following, the block's otherwise. */
          const toggleRowBranching = () => {
            if (!nesting) { toggleConditionalCorrectness(!isEnabled); return; }
            setActiveBranchIndex(0);
            scenario.writeActiveRow((effEnabled
              ? { conditionalEquation: undefined, conditionalBranches: undefined }
              : { conditionalEquation: effEquation ?? '',
                  conditionalBranches: [makeRow('migrate')] }) as never);
          };
          /*
           * WHERE THE TOGGLE SITS IS A CLAIM ABOUT WHAT IT APPLIES TO. On a follower it turns
           * branching on for ONE ROW, so standing above the scenario box reads as "every scenario".
           */
          const branchingToggle = showConditionalUI ? (
            <BranchingCorrectsRow
              checked={effEnabled}
              onToggle={toggleRowBranching}
            />
          ) : null;
          const blockLevelToggle = showConditionalUI && !nesting;
          const branches = conditionalBranches ?? [];
          const clampedIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
          // Graded only, like every other branching block's own local. `showConditionalUI` above
          // already hides the carousel outside graded, but this drives `pointsToRender` and the
          // role-click routing too — so a branch set left over from a mode switch would still
          // resolve here, against roles the mode does not consult.
          const activeBranchLocal = isEnabled && gradingMode === 'graded' ? branches[clampedIndex] : undefined;
          const activeRow = scenario.activeScenario ?? activeBranchLocal;
          const hasEquation = !!conditionalEquation?.trim();
          // minSampleValues, NOT `rolledValues` — that stays {} until the block is selected, which
          // would make every equation referencing a variable look invalid the whole time it is
          // being authored.
          const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;
          const equationWorks = hasEquation && !equationInvalid;

          // One rows list, fed either the static rows or the branch-scoped ones and a matching role
          // click handler. A branch only ever remaps `role` — never a label, never identity — so
          // there is no second list to keep in step.
          // One list, fed either the static points or the branch-scoped ones and a matching role
          // click handler. A branch only ever remaps `role` — never a label, never identity — so
          // there is no second list to keep in step.
          const pointsToRender = activeRow ? effectiveValuesForActive : rolesForMode(gradingMode, values);
          const pointListJsx = (
            // The list as a whole. A scale point is identified by its VALUE, not by an id, so the
            // diff names `values` rather than a row — there is nothing finer to point at, and
            // adding or removing a point changes the same key as editing one.
            <div data-field-id='values' className='flex flex-col gap-1.5'>
              {pointsToRender.map((point, idx) => (
                <ScalePointRow
                  key={point.value}
                  point={point}
                  isLast={idx === pointsToRender.length - 1}
                  canDelete={values.length > SCALE_MIN_VALUES}
                  armed={armedPoint === point.value}
                  setArmed={on => setArmedPoint(on ? point.value : null)}
                  onLabelChange={handleLabelChange}
                  roleLocked={gradingMode !== 'graded'}
                  onRoleClick={scenario.activeScenario ? handleScenarioValueRoleClick
                    : activeBranchLocal
                      ? (value) => handleBranchValueRoleClick(activeBranchLocal.id, value)
                      : handleValueRoleClick}
                  onRemove={removeLastPoint}
                  labelEditorsRef={labelEditorsRef}
                  activeEditorRef={activeEditorRef}
                  setActiveEditor={setActiveEditor}
                />
              ))}
            </div>
          );

          const addPointButton = (
            <AddRowButton
              label={t('blocks.options.addOption')}
              onClick={addPoint}
              disabled={values.length >= SCALE_MAX_VALUES}
            />
          );

          return (
            <>
              {blockLevelToggle && <div className='flex flex-col gap-2 pl-1'>{branchingToggle}</div>}

              {showConditionalUI && isEnabled && activeBranchLocal ? (
                <BranchConditionCarousel
                  seedVariable={(() => { const n = variableChipNames(questionHtml); return n.length === 1 ? n[0] : undefined; })()}
                  ref={branchPrevButtonRef}
                  equation={conditionalEquation ?? ''}
                  onEquationChange={updateConditionalEquation}
                  variables={variables}
                  rolledValues={rolledValues}
                  angleMode={angleMode}
                  equationInvalid={equationInvalid}
                  equationWorks={equationWorks}
                  hasError={editorIssues.some(i => i.severity === 'error' && ['no-equation', 'invalid-equation', 'coverage-gap'].includes(i.key))}
                  branches={branches}
                  activeIndex={clampedIndex}
                  onActiveIndexChange={setActiveBranchIndex}
                  onBranchChange={updateConditionalBranch}
                  onAdd={addConditionalBranch}
                  onRemove={removeConditionalBranch}
                  onFieldFocus={disconnectSharedToolbar}
                >
                  {/* The border is branch scoping — it shows where "editing this branch" starts
                      and stops, and carries the branch's own error ring. It does NOT scroll: this
                      used to be a rows x values matrix that could get genuinely tall, but a single
                      axis is the same list the un-branched path renders without a scroller, and a
                      second scrollbar inside the panel's own is worse than a longer panel. */}
                  <div className='flex flex-col gap-4'>
                    {pointListJsx}
                    {addPointButton}
                  </div>
                </BranchConditionCarousel>
              ) : (
                /* The scenario's own box — the carousel's shape with a variant in place of an
                   equation, and nothing at all when this block follows nothing. */
                <ScenarioSetupFrame
                  scenario={scenario.activeScenario}
                  name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
                  pager={scenario.pager}
                  unreviewedCount={scenario.unreviewedCount}
                  onDismissUnreviewed={scenario.dismissUnreviewed}
                >
                  {/* Only when there IS a box: the frame passes children through unwrapped when this
                      block follows nothing, so an ungated toggle renders a SECOND copy here. */}
                  {nesting && branchingToggle && <div className='pb-2'>{branchingToggle}</div>}
                  {showConditionalUI && effEnabled && effActiveBranch ? (
                    <BranchConditionCarousel
                      seedVariable={(() => { const n = variableChipNames(questionHtml); return n.length === 1 ? n[0] : undefined; })()}
                      equation={effEquation ?? ''}
                      onEquationChange={v => scenario.writeActiveRow({ conditionalEquation: v } as never)}
                      variables={variables}
                      rolledValues={rolledValues}
                      angleMode={angleMode}
                      equationInvalid={false}
                      equationWorks={!!effEquation?.trim()}
                      hasError={false}
                      branches={effBranches}
                      activeIndex={effIndex}
                      onActiveIndexChange={setActiveBranchIndex}
                      onBranchChange={(id, patch) => scenario.writeActiveRow({
                        conditionalBranches: effBranches.map(b => (b.id === id ? { ...b, ...patch } : b)) } as never)}
                      onAdd={() => scenario.writeActiveRow({
                        conditionalBranches: [...effBranches, makeRow('fork')] } as never)}
                      onRemove={id => scenario.writeActiveRow({
                        conditionalBranches: effBranches.filter(b => b.id !== id) } as never)}
                      onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    >
                  <div className='flex flex-col gap-4'>
                    {pointListJsx}
                    {addPointButton}
                  </div>
                    </BranchConditionCarousel>
                  ) : (<>
                  <div className='flex flex-col gap-4'>
                    {pointListJsx}
                    {addPointButton}
                  </div>
                  </>)}
                </ScenarioSetupFrame>
              )}
            </>
          );
          })()}
          </DrivenRowFrame>
          </div>
      }
    />
  );
}
